import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface MonthlySnapshot {
  id: string;
  month: number;
  year: number;
  total_hours: number;
  active_hours: number;
  active_student_count: number;
  total_teaching_hours: number;
  sudden_leave_hours: number;
  sudden_leave_paid: number;
  submitted_late: number;
  total_reports: number;
  total_teacher_salary: number;
  total_teacher_commission: number;
  total_trial_costs: number;
  teaching_hours_online: number;
  teaching_hours_siam: number;
  teaching_hours_ngamwongwan: number;
  salary_online: number;
  salary_siam: number;
  salary_ngamwongwan: number;
  course_ended_student_count: number;
  course_student_count: number;
  not_continue_student_count: number;
  created_at: string;
}

export interface SnapshotComparison {
  current: MonthlySnapshot | null;
  previous: MonthlySnapshot | null;
  percentChanges: Record<string, number | null>;
}

function calculatePercentChange(current: number | null, previous: number | null): number | null {
  if (previous === null || previous === 0 || current === null) return null;
  return ((current - previous) / previous) * 100;
}

export function useMonthlySnapshot(selectedMonth?: number, selectedYear?: number) {
  return useQuery({
    queryKey: ["monthly-snapshot", selectedMonth, selectedYear],
    queryFn: async (): Promise<SnapshotComparison> => {
      const { data, error } = await (supabase as any)
        .schema("reporting")
        .from("fishsix_monthly_snapshot")
        .select("*")
        .order("year", { ascending: false })
        .order("month", { ascending: false })
        .limit(12);
      
      if (error) throw error;
      
      const snapshots = (data as unknown as MonthlySnapshot[]) || [];
      return processSnapshots(snapshots, selectedMonth, selectedYear);
    },
  });
}

function processSnapshots(
  data: MonthlySnapshot[], 
  selectedMonth?: number, 
  selectedYear?: number
): SnapshotComparison {
  let current: MonthlySnapshot | null = null;
  let previous: MonthlySnapshot | null = null;

  if (selectedMonth && selectedYear) {
    current = data.find(d => d.month === selectedMonth && d.year === selectedYear) || null;
    const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
    const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
    previous = data.find(d => d.month === prevMonth && d.year === prevYear) || null;
  } else {
    current = data[0] || null;
    previous = data[1] || null;
  }

  const percentChanges: Record<string, number | null> = {};
  
  if (current && previous) {
    const metricsToCompare = [
      "total_hours",
      "active_hours",
      "active_student_count",
      "total_teaching_hours",
      "sudden_leave_hours",
      "sudden_leave_paid",
      "total_teacher_salary",
      "total_teacher_commission",
      "total_trial_costs",
      "teaching_hours_online",
      "teaching_hours_siam",
      "teaching_hours_ngamwongwan",
      "salary_online",
      "salary_siam",
      "salary_ngamwongwan",
      "course_ended_student_count",
      "not_continue_student_count",
    ];

    metricsToCompare.forEach(metric => {
      const currVal = (current as any)[metric];
      const prevVal = (previous as any)[metric];
      percentChanges[metric] = calculatePercentChange(currVal, prevVal);
    });

    const currLatePercent = current.total_reports > 0 
      ? (current.submitted_late / current.total_reports) * 100 
      : 0;
    const prevLatePercent = previous.total_reports > 0 
      ? (previous.submitted_late / previous.total_reports) * 100 
      : 0;
    percentChanges["late_percent"] = calculatePercentChange(currLatePercent, prevLatePercent);
  }

  return { current, previous, percentChanges };
}

export function useAllSnapshots() {
  return useQuery({
    queryKey: ["all-monthly-snapshots"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema("reporting")
        .from("fishsix_monthly_snapshot")
        .select("month, year")
        .order("year", { ascending: false })
        .order("month", { ascending: false });

      if (error) throw error;
      return (data as unknown as { month: number; year: number }[]) || [];
    },
  });
}
